import {
  createRouter, createWebHistory, RouteLocationNormalized, RouteMeta,
} from 'vue-router';
import { Logger, User } from 'oidc-client-ts';
import UsersPage from '@/views/Users/index.vue';
import OpenIdConnectCallback from '@/views/OpenIdConnectCallback.vue';
import ErrorPage from '@/views/ErrorPage.vue';
import AddUserPage from '@/views/Users/add/index.vue';
import { i18n, setAppLocale, toSupportedLocale } from '@/lang';
import EditUserPage from '@/views/Users/edit/index.vue';
import userManager from '@/components/oidc';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // set locale in path
      path: '/',
      redirect: () => ({ path: `/${i18n.global.locale}/members` }),
      alias: '/members',
    }, {
      path: '/:locale/members',
      component: UsersPage,
      meta: { requiredScopes: ['profile', 'dealer_admin'], requiredClaims: ['admin_roles', 'dealer_org_id'] },
    }, {
      path: '/:locale/members/:id([\\d\\w-_]+)',
      component: EditUserPage,
      meta: { requiredScopes: ['profile', 'dealer_admin'], requiredClaims: ['admin_roles'] },
    }, {
      path: '/:locale/members/create',
      component: AddUserPage,
      meta: { requiredScopes: ['profile', 'dealer_admin'], requiredClaims: ['admin_roles', 'dealer_org_id'] },
    },
    {
      name: 'licenses',
      path: '/:locale/licenses',
      component: () => import('@/views/Licenses/index.vue'),
      meta: { requiredScopes: ['dealer_admin'], requiredClaims: ['admin_roles'], requiredRole: ['superAdmin'] },
    },
    {
      name: 'roles',
      path: '/:locale/roles',
      component: () => import('@/views/Roles/index.vue'),
      meta: { requiredScopes: ['dealer_admin'], requiredClaims: ['admin_roles'], requiredRole: ['superAdmin'] },
    },
    {
      name: 'sync-org',
      path: '/:locale/sync/organizations',
      component: () => import('@/views/Organizations/checkSync/index.vue'),
      meta: {
        requiredScopes: ['dealer_admin'],
        requiredClaims: ['admin_roles'],
        requiredRole: ['superAdmin', 'orgSuperAdmin'],
      },
    },
    {
      name: 'apps',
      path: '/:locale/applications',
      component: () => import('@/views/Applications/index.vue'),
      meta: { requiredScopes: ['dealer_admin'], requiredClaims: ['admin_roles'] },
    },
    {
      name: 'app',
      path: '/:locale/applications/:id([\\d\\w-_]+)',
      component: () => import('@/views/Applications/edit/index.vue'),
      meta: { requiredScopes: ['dealer_admin'], requiredClaims: ['admin_roles'] },
    },
    { path: '/openid-connect-callback', component: OpenIdConnectCallback },
    { path: '/error', redirect: () => ({ path: `/${i18n.global.locale}/error` }) },
    { path: '/:locale/error', component: ErrorPage },
    { path: '/access-denied', redirect: () => ({ path: `/${i18n.global.locale}/access-denied` }) },
    { path: '/:locale/access-denied', component: ErrorPage, props: { status: 403 } },
    { path: '/:matchAll(.*)', component: ErrorPage, props: { status: 404 } }, // 404 - useless - better let SL handle it
  ],
});

const hasAccess = (user: User, meta: RouteMeta) => {
  const requiredScopes = meta.requiredScopes as Array<string>;
  if (requiredScopes && user.scopes && !requiredScopes.every((s) => user.scopes.includes(s))) {
    Logger.error('router', `required scopes [${requiredScopes}] not found in [${user.scopes}]`);
    return false;
  }
  const requiredClaims = meta.requiredClaims as Array<string>;
  if (requiredClaims && !requiredClaims.every((claim) => !!user.profile[claim])) {
    Logger.error('router', `required claims [${requiredClaims}] not found`);
    return false;
  }
  const requiredRole = meta.requiredRole as Array<string>;
  if (requiredRole && !requiredRole.includes(user.profile.admin_roles as string)) {
    Logger.error('router', `required role [${requiredRole}] not found`);
    return false;
  }
  Logger.debug('router', `user <${user.profile.email}> has access`);
  return true;
};

// /**
//  * validate token before use
//  * @param user
//  */
// const haveValidToken = async (user: User) => {
//   if (user.expired) {
//      await userManager.removeUser();
//      Logger.info('router', 'removed expired user from store');
//     return false;
//   }
//
//   return window.tokenChecked;
//
//   /* if (!window.tokenChecked) {
//       Logger.debug('router', 'validating token...');
//       return userManager.userInfo(user.access_token).then((userData) => {
//         // save principal in store
//         useAppStore().setPrincipal(user);
//
//         window.tokenChecked = !!userData;
//         return window.tokenChecked;
//       }).catch(async () => {
//         await userManager.removeUser();
//         Logger.info('router', 'removed invalid user from store');
//       });
//     }
//     return true; */
// };

/**
 * change URL to the new locale. (example: /en/members -> /de-CH/members)
 */
export const forwardToLocale = (locale: string, route: RouteLocationNormalized) => {
  // Logger.info('router',`forwardToLocale(${locale})`);
  const pathLocale = route.params.locale as string;
  if (locale && pathLocale && locale !== pathLocale) {
    const newPath = route.fullPath.replace(`/${pathLocale}`, `/${locale}`);
    router.push(newPath);
  }
};

/**
 * router interceptor for checking the roles & starting the openid-connect protocol
 * @see https://github.com/joaojosefilho/vuejsOidcClient
 */
router.beforeEach((to, _from, next) => {
  let locale = to.params.locale as string ?? 'en';
  Logger.debug('router', `to:${to.path} global-locale:${i18n.global.locale} (path-locale:${to.params.locale})`);

  if (locale !== i18n.global.locale) {
    locale = toSupportedLocale(locale, 'en');
    if (locale !== i18n.global.locale) {
      locale = setAppLocale(locale);
      forwardToLocale(locale, to);
    }
  }

  // handle authorization
  const routeMeta = to.matched && to.matched.length > 0 ? to.matched[0].meta : null;
  if (routeMeta && (routeMeta.requiredScopes || routeMeta.requiredClaim)) {
    Logger.debug('router', `protected route ${to.path} ${to.params}`);

    // AM doesn't interpret locale: 'ro-RO' is not evaluated to  'ro', but defaults to 'en'
    // const locale = to.query.locale as string ?? undefined

    userManager.getUser().then(
      async (unsafeUser) => {
        // if user not logged in
        if (!unsafeUser || !window.tokenChecked) {
          Logger.info('router', 'starting login...');
          await userManager.startLogin(to.fullPath, locale);
          return; // should not be reached
        }

        let user = unsafeUser;
        if (unsafeUser.expired) {
          Logger.info('router', 'renewing token...');
          await userManager.getValidUser().then((freshUser) => {
            Logger.info('router', `fresh user: ${freshUser}`);
            user = freshUser as User;
          });
        }

        // at this point we should have a valid user
        if (!user || user.expired) throw new Error('no valid user');

        if (hasAccess(user, routeMeta)) {
          Logger.info('router', `user:<${user.profile.given_name}> role:[${user.profile.admin_roles}]`);
          next();
        } else {
          Logger.error('router', `no access to ${to.path}`);
          next('/access-denied');
        }
      },
      (err) => {
        Logger.info('router', `login failed: ${err}`);
        next('/access-denied');
      },
    );
  } else {
    Logger.debug('router', `public route ${to.path}`);
    next();
  }
});

export default router;
